import type { LucideIcon } from "lucide-react";
import { BookOpen, ChevronsLeft, ChevronsRight, FileBarChart, Home, LayoutDashboard, LifeBuoy, LogOut, Shield, Users, X } from "lucide-react";
import { useMsal } from "@azure/msal-react";
import type { SidebarSection } from "../types";
import { LOGO_DATA_URI } from "../assets/logo";

export interface SidebarItem {
  icon: LucideIcon;
  label: string;
  section?: SidebarSection;
}
export interface SidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  registerTourTarget: (key: string) => (el: HTMLElement | null) => void;
  activeSection: SidebarSection;
  onSelectSection: (section: SidebarSection) => void;
}
export function Sidebar({ collapsed, onToggleCollapsed, mobileOpen, onCloseMobile, registerTourTarget, activeSection, onSelectSection }: SidebarProps) {

  const { instance } = useMsal();

  const handleSignOut = () => {
    sessionStorage.clear();
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
    });
  };

  const items: SidebarItem[] = [
    { icon: LayoutDashboard, label: "Dashboard", section: "analytics" },
    { icon: Home, label: "Healthcare Lookup", section: "lookup" },
    { icon: BookOpen, label: "Resources", section: "resources" },
    { icon: FileBarChart, label: "Reports", section: "reports" },
    { icon: Users, label: "User Management" },
    { icon: Shield, label: "Facility Management" },
    { icon: LifeBuoy, label: "System Support", section: "systemSupport" },
  ];

  const navContent = (isMobile: boolean) => (
    <>
      <div className={`flex h-16 items-center border-b border-slate-200 ${!isMobile && collapsed ? "justify-center px-2" : "gap-2.5 px-5"}`}>
        <img src={LOGO_DATA_URI} alt="FL-Link logo" className="h-9 w-9 shrink-0 object-contain" />
        {(isMobile || !collapsed) && (
          <div className="leading-tight">
            <p className="text-sm font-semibold text-slate-900">FL-Link</p>
            <p className="text-[11px] text-slate-400">Florida Linkage, Information &amp; Navigation to Care Portal</p>
          </div>
        )}
        {isMobile && (
          <button onClick={onCloseMobile} className="ml-auto text-slate-400 hover:text-slate-700" title="Close menu">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
      <nav ref={!isMobile ? registerTourTarget("sidebarNav") : undefined} className="flex-1 space-y-0.5 px-3 py-4">
        {items.map((it) => {
          const isActive = it.section != null && it.section === activeSection;
          return (
            <button
              key={it.label}
              onClick={() => {
                if (it.section) onSelectSection(it.section);
                if (isMobile) onCloseMobile();
              }}
              title={!isMobile && collapsed ? it.label : undefined}
              className={`flex w-full items-center rounded-lg py-2 text-sm font-medium transition ${!isMobile && collapsed ? "justify-center px-0" : "gap-3 px-3"
                } ${isActive ? "bg-cyan-50 text-cyan-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}
            >
              <it.icon className="h-4 w-4 shrink-0" />
              {(isMobile || !collapsed) && it.label}
            </button>
          );
        })}
      </nav>
      <div className="border-t border-slate-200 p-3">
        {!isMobile && (
          <button
            onClick={onToggleCollapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`flex w-full items-center rounded-lg py-2 text-sm font-medium text-slate-500 transition hover:bg-slate-50 hover:text-slate-900 ${collapsed ? "justify-center px-0" : "gap-3 px-3"
              }`}
          >
            {collapsed ? <ChevronsRight className="h-4 w-4 shrink-0" /> : <ChevronsLeft className="h-4 w-4 shrink-0" />}
            {!collapsed && "Collapse"}
          </button>
        )}
        <button
          onClick={handleSignOut}
          title={!isMobile && collapsed ? "Sign out" : undefined}
          className={`mt-1 flex w-full items-center rounded-lg py-2 text-sm font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-900 ${!isMobile && collapsed ? "justify-center px-0" : "gap-3 px-3"
            }`}
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {(isMobile || !collapsed) && "Sign out"}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar — collapses to an icon-only rail */}
      <aside
        className={`hidden ${collapsed ? "w-16" : "w-60"} shrink-0 flex-col border-r border-slate-200 bg-white transition-all duration-200 md:flex`}
      >
        {navContent(false)}
      </aside>

      {/* Mobile sidebar — slide-in drawer with backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={onCloseMobile} />
          <aside className="relative flex h-full w-64 flex-col bg-white shadow-xl">
            {navContent(true)}
          </aside>
        </div>
      )}
    </>
  );
}

